export interface MethodologyArticle {
  slug: string;
  title: string;
  h1: string;
  metaDescription: string;
  category: "Data Integrity" | "Hiring Signals" | "Safety & Privacy" | "Scoring Methodology";
  lastUpdatedDate: string;
  summary: string;
  sections: {
    heading: string;
    paragraphs: string[];
    bulletPoints?: string[];
  }[];
  relatedFeatureSlugs: string[];
}

export const METHODOLOGY_REGISTRY: Record<string, MethodologyArticle> = {
  "hiring-signals": {
    slug: "hiring-signals",
    title: "Hiring Signals Methodology & Calculation | BizNearYou",
    h1: "BizNearYou Hiring Signals Evaluation Methodology",
    metaDescription: "Detailed technical explanation of how BizNearYou evaluates public commercial growth indicators to estimate employer hiring momentum.",
    category: "Hiring Signals",
    lastUpdatedDate: "2026-07-20",
    summary: "Hiring Signals evaluate public operational indicators—such as review velocity, website updates, and physical facility expansion—to identify growing local businesses.",
    sections: [
      {
        heading: "1. Data Collection & Public Signal Aggregation",
        paragraphs: [
          "BizNearYou aggregates publicly accessible commercial business indicators from state entity filings, Google Maps place records, domain WHOIS timestamps, and business web presences.",
          "We evaluate operational growth patterns rather than scraping illegal or private employee communications."
        ]
      },
      {
        heading: "2. Signal Categories & Weighting Matrix",
        paragraphs: [
          "Our scoring model categorizes operational growth into three weighted signal tiers:"
        ],
        bulletPoints: [
          "Tier 1: Review Velocity & Customer Engagement (35% weight) - Consistent review growth signals active client volume.",
          "Tier 2: Physical Footprint & Facility Updates (35% weight) - Secondary branch openings, new equipment additions, or service area extensions.",
          "Tier 3: Digital Presence & Service Line Additions (30% weight) - New website services, staff additions, or partnership press releases."
        ]
      },
      {
        heading: "3. Signal Confidence Levels & Limitations",
        paragraphs: [
          "A 'High' Hiring Signal indicates strong recent commercial expansion. It does NOT guarantee that an employer has an active budget for a specific role.",
          "Candidates must independently verify employer needs during direct outreach."
        ]
      }
    ],
    relatedFeatureSlugs: ["hiring-signals", "opportunity-score"]
  },
  "opportunity-score": {
    slug: "opportunity-score",
    title: "Opportunity Score Rating Methodology | BizNearYou",
    h1: "BizNearYou Opportunity Score Calculation Framework",
    metaDescription: "Learn how the Opportunity Score (1-100) measures local employer contactability, operational stability, and outreach conversion potential.",
    category: "Scoring Methodology",
    lastUpdatedDate: "2026-07-18",
    summary: "The Opportunity Score synthesizes contact completeness, phone validity, website status, and review volume into an actionable 1-100 score.",
    sections: [
      {
        heading: "1. Core Scoring Components",
        paragraphs: [
          "The Opportunity Score combines four fundamental operational health dimensions:"
        ],
        bulletPoints: [
          "Contactability Index (40 pts): Verified office phone number, domain email, or direct contact form.",
          "Business Longevity & Rating Stability (25 pts): Continuous operation years and steady customer rating history.",
          "Digital Presence Quality (20 pts): Active website, SSL encryption, and mobile usability.",
          "Growth Signal Momentum (15 pts): Recent review spikes or expanded service lines."
        ]
      }
    ],
    relatedFeatureSlugs: ["opportunity-score", "contact-search"]
  },
  "data-sources": {
    slug: "data-sources",
    title: "Data Sources & Verification Standards | BizNearYou",
    h1: "BizNearYou Data Sources & Information Verification",
    metaDescription: "Transparent overview of data sources used by BizNearYou, including public registries, Google Maps Platform APIs, and domain audits.",
    category: "Data Integrity",
    lastUpdatedDate: "2026-07-22",
    summary: "BizNearYou relies on verified public APIs, state business registries, and official domain records to maintain real, physically operating local employer profiles.",
    sections: [
      {
        heading: "1. Primary Data Providers",
        paragraphs: [
          "We compile local commercial profiles using legitimate, privacy-compliant sources:"
        ],
        bulletPoints: [
          "Google Maps Platform Places API: Verified physical business coordinates, operating hours, and telephone numbers.",
          "State Business Entity Registries: Public corporate filings, active license standing, and registered agent details.",
          "Official Commercial Websites: Publicly accessible services, staff listings, and contact form endpoints."
        ]
      }
    ],
    relatedFeatureSlugs: ["local-business-discovery", "website-review"]
  },
  "accuracy-and-limitations": {
    slug: "accuracy-and-limitations",
    title: "Accuracy, Freshness & System Limitations | BizNearYou",
    h1: "Data Accuracy Standards & Known System Limitations",
    metaDescription: "Clear disclosure of BizNearYou data update frequencies, verified information limits, and guidance on independent candidate verification.",
    category: "Data Integrity",
    lastUpdatedDate: "2026-07-20",
    summary: "BizNearYou maintains transparent standards regarding data freshness, known API sync delays, and the necessity for independent job seeker verification.",
    sections: [
      {
        heading: "1. Data Refresh Cycles",
        paragraphs: [
          "Local business information changes dynamically. BizNearYou updates place profiles, telephone numbers, and website links on 30-day rolling cycles.",
          "Because small businesses change operating hours or management without notice, candidates should always call or visit to confirm details."
        ]
      },
      {
        heading: "2. What We Guarantee vs. What Requires User Verification",
        paragraphs: [
          "We guarantee that indexed businesses represent real, physically operating commercial entities.",
          "We do NOT guarantee specific salary offers, instant response times, or immediate job availability."
        ]
      }
    ],
    relatedFeatureSlugs: ["local-business-discovery", "opportunity-score"]
  },
  "safety": {
    slug: "safety",
    title: "Employment Safety & Scam Protection Standards | BizNearYou",
    h1: "BizNearYou Employment Safety & Scam Prevention Framework",
    metaDescription: "Our strict anti-scam safeguards, user privacy protections, zero-fee policy, and employer vetting guidelines.",
    category: "Safety & Privacy",
    lastUpdatedDate: "2026-07-23",
    summary: "BizNearYou protects job seekers through strict local business physical verification, zero-fee enforcement, and zero tolerance for work-from-home scams.",
    sections: [
      {
        heading: "1. Strict Physical Business Verification",
        paragraphs: [
          "Unlike generic job boards that allow anonymous remote accounts to post fake listings, BizNearYou ONLY indexes real, physically operating commercial enterprises with verifiable commercial addresses."
        ]
      },
      {
        heading: "2. Zero Fees & Privacy Safeguards",
        paragraphs: [
          "BizNearYou never charges job seekers to search, contact, or apply to employers.",
          "We never sell candidate personal contact details to third-party marketing brokers."
        ]
      }
    ],
    relatedFeatureSlugs: ["local-business-discovery"]
  },
  "why-businesses-before-job-listings": {
    slug: "why-businesses-before-job-listings",
    title: "Why Businesses Appear Before Job Listings | BizNearYou",
    h1: "Why BizNearYou Displays Businesses Before Public Job Postings",
    metaDescription: "Understand why local small business hiring often occurs without being posted on public job boards and how mapping businesses unlocks unadvertised roles.",
    category: "Hiring Signals",
    lastUpdatedDate: "2026-07-24",
    summary: "Much of local small business hiring occurs informally before job requisitions are published. BizNearYou maps physical employers to allow direct candidate outreach during early expansion phases.",
    sections: [
      {
        heading: "1. The Economics of Local Business Hiring",
        paragraphs: [
          "Posting job ads on commercial job boards costs $300 to $1,000+ per month per posting. Small local businesses—such as medical practices, plumbing contractors, and auto repair shops—often delay or skip formal job postings.",
          "When a small business experiences workload expansion, the owner or office manager typically asks current employees for referrals, accepts walk-in resumes, or responds to direct email inquiries."
        ]
      },
      {
        heading: "2. The 'First Mover' Advantage for Job Seekers",
        paragraphs: [
          "Candidates who wait for a job ad to appear on a national portal compete against 100 to 500+ applicants.",
          "Candidates who discover expanding local businesses on BizNearYou and reach out directly enjoy 1-on-1 conversations with decision makers before any job advertisement is created."
        ]
      },
      {
        heading: "3. What Candidates Should and Should Not Assume",
        paragraphs: [
          "A business listed on BizNearYou represents a real, verified local commercial entity.",
          "It does NOT guarantee an immediate open requisition or budget for every position. Candidates must inquire politely to learn about current or upcoming staffing needs."
        ]
      }
    ],
    relatedFeatureSlugs: ["local-business-discovery", "hiring-signals"]
  }
};

export function getAllMethodologyArticles(): MethodologyArticle[] {
  return Object.values(METHODOLOGY_REGISTRY);
}

export function getMethodologyArticleBySlug(slug: string): MethodologyArticle | null {
  if (!slug) return null;
  const cleanSlug = slug.trim().toLowerCase();
  return METHODOLOGY_REGISTRY[cleanSlug] || null;
}
